import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { Conversation, MessageSender } from './chatbot.model';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root',
})
export class ChatbotService {
  readonly show$: Observable<boolean>;
  readonly conversations$: Observable<Conversation[]>;
  readonly currentConversation$: Observable<Conversation | null>;
  readonly isCurrentConversationLoading$: Observable<boolean>;
  readonly isConversationsLoading$: Observable<boolean>;
  readonly isMessageLoading$: Observable<boolean>;

  private readonly _show$: Subject<boolean> = new Subject();
  private readonly _conversations$: BehaviorSubject<Conversation[]> =
    new BehaviorSubject<Conversation[]>([]);
  private readonly _isConversationsLoading$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private readonly _isMessageLoading$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private readonly _currentConversation$: BehaviorSubject<Conversation | null> =
    new BehaviorSubject<Conversation | null>(null);
  private readonly _isCurrentConversationLoading$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  constructor() {
    this.show$ = this._show$.asObservable();
    this.conversations$ = this._conversations$.asObservable();
    this.isConversationsLoading$ = this._isConversationsLoading$.asObservable();
    this.isMessageLoading$ = this._isMessageLoading$.asObservable();
    this.currentConversation$ = this._currentConversation$.asObservable();
    this.isCurrentConversationLoading$ =
      this._isCurrentConversationLoading$.asObservable();
  }

  showChatbot(): void {
    this._show$.next(true);
  }

  hideChatbot(): void {
    this._show$.next(false);
  }

  getConversations(): void {
    this._isConversationsLoading$.next(true);
    setTimeout(() => {
      this._isConversationsLoading$.next(false);
    }, 500);
  }

  initializeConversations(): void {
    this._isConversationsLoading$.next(true);
    setTimeout(() => {
      this._conversations$.next([
        {
          id: '1',
          messages: [
            {
              id: '1',
              sender: MessageSender.AI_ASSISTANT,
              content: 'Hi! How may I help you?',
              timestamp: new Date('2025-04-04T12:00:00Z'),
            },
            {
              id: '2',
              sender: MessageSender.USER,
              content: 'How can I invest in Greece?',
              timestamp: new Date('2025-04-04T12:01:00Z'),
            },
            {
              id: '3',
              sender: MessageSender.USER,
              content: 'provide me with more details',
              timestamp: new Date('2025-04-04T12:02:00Z'),
            },
            {
              id: '4',
              sender: MessageSender.USER,
              content:
                'At the same time, I want to know the investment cost and the minimum investment amount for a property in Greece',
              timestamp: new Date('2025-04-04T12:03:00Z'),
            },
            {
              id: '5',
              sender: MessageSender.AI_ASSISTANT,
              content: 'Here you can find the details you requested',
              timestamp: new Date('2025-04-04T12:04:00Z'),
            },
          ],
          timestamp: new Date('2025-04-04T12:00:00Z'),
          hasCompleted: false,
        },
        {
          id: '3',
          messages: [
            {
              id: '1',
              sender: MessageSender.AI_ASSISTANT,
              content: 'Hi there! How can I assist you today?',
              timestamp: new Date('2025-04-04T15:00:00Z'),
            },
            {
              id: '2',
              sender: MessageSender.USER,
              content: 'I’m interested in investing in Greece.',
              timestamp: new Date('2025-04-04T15:00:30Z'),
            },
            {
              id: '3',
              sender: MessageSender.AI_ASSISTANT,
              content:
                'That’s great! Are you looking into real estate, business, or another type of investment?',
              timestamp: new Date('2025-04-04T15:00:50Z'),
            },
            {
              id: '4',
              sender: MessageSender.USER,
              content:
                'Real estate mainly. I’d like to know the minimum investment required.',
              timestamp: new Date('2025-04-04T15:01:20Z'),
            },
            {
              id: '5',
              sender: MessageSender.AI_ASSISTANT,
              content:
                'In Greece, the minimum investment for the Golden Visa through real estate is €250,000.',
              timestamp: new Date('2025-04-04T15:01:45Z'),
            },
            {
              id: '6',
              sender: MessageSender.USER,
              content: 'Does that apply to all areas of Greece?',
              timestamp: new Date('2025-04-04T15:02:10Z'),
            },
            {
              id: '7',
              sender: MessageSender.AI_ASSISTANT,
              content:
                'Not exactly. In some high-demand areas like parts of Athens, Mykonos, and Santorini, the minimum threshold has increased to €500,000.',
              timestamp: new Date('2025-04-04T15:02:35Z'),
            },
            {
              id: '8',
              sender: MessageSender.USER,
              content: 'What about expected returns and costs involved?',
              timestamp: new Date('2025-04-04T15:03:00Z'),
            },
            {
              id: '9',
              sender: MessageSender.AI_ASSISTANT,
              content:
                'Rental yields in Greece vary by location but generally range between 3% and 6%. You should also account for property taxes, legal fees (~2–3%), and notary costs (~1%).',
              timestamp: new Date('2025-04-04T15:03:30Z'),
            },
            {
              id: '10',
              sender: MessageSender.USER,
              content:
                'This is really helpful. Can you show me available properties under €300,000?',
              timestamp: new Date('2025-04-04T15:04:00Z'),
            },
            {
              id: '11',
              sender: MessageSender.AI_ASSISTANT,
              content:
                'Sure! Here’s a list of available properties under €300,000 in Athens and Thessaloniki.',
              timestamp: new Date('2025-04-04T15:04:25Z'),
            },
          ],
          timestamp: new Date('2025-04-04T15:00:00Z'),
          hasCompleted: true,
        },
      ]);
      this._isConversationsLoading$.next(false);
    }, 500);
  }

  startNewConversation(): void {
    this._isCurrentConversationLoading$.next(true);

    setTimeout(() => {
      const newConversation: Conversation = {
        id: uuidv4(),
        messages: [],
        timestamp: new Date(),
        hasCompleted: false,
      };

      this._conversations$.next([
        newConversation,
        ...this._conversations$.getValue(),
      ]);

      this._currentConversation$.next(newConversation);

      this._isCurrentConversationLoading$.next(false);
    }, 500);
  }

  sendMessage(conversationId: string, content: string): void {
    this._isMessageLoading$.next(true);

    const message = {
      id: uuidv4(),
      sender: MessageSender.USER,
      content: content,
      timestamp: new Date(),
    };

    const conversation = this._currentConversation$.getValue();
    conversation?.messages.push(message);

    this._currentConversation$.next(conversation);

    setTimeout(() => {
      // answer from AI

      const message = {
        id: uuidv4(),
        sender: MessageSender.AI_ASSISTANT,
        content: 'Please wait a moment while I process your request.',
        timestamp: new Date(),
      };

      const conversation = this._currentConversation$.getValue();
      conversation?.messages.push(message);

      this._currentConversation$.next(conversation);

      this._isMessageLoading$.next(false);
    }, 500);
  }

  clearConversation(): void {
    this._currentConversation$.next(null);
  }
}
